import React, { memo, Suspense, lazy } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import { useSelector } from "react-redux";
import { canUseRebranding } from "./component/utils/Constants";
import Header from "./component/includes/Header";
import PageNotFound from "./component/pages/PageNotFound";
import { SpinnerLoader } from "./component/utils/Loading";
import WhitelabelFavicon from "./component/includes/WhitelabelFavicon";

// Route configuration types for better type safety
interface RouteConfig {
  path: string;
  component:
    | React.LazyExoticComponent<React.ComponentType<any>>
    | React.ComponentType<any>;
  exact?: boolean;
  // Rebranding service page — redirected away when the account's package
  // excludes it (see canUseRebranding).
  rebranding?: boolean;
}

interface RouteGroup {
  name: string;
  routes: RouteConfig[];
}

// Lazy loading components for code splitting - grouped by feature
const PublicComponents = {
  Login: lazy(() => import("./component/pages/Login")),
  ReferralJoin: lazy(() => import("./component/pages/ReferralJoin")),
  BookFreeDemo: lazy(() => import("./component/pages/BookFreeDemo")),
  DemoBookingConfirmed: lazy(() => import("./component/pages/DemoBookingConfirmed")),
  DemoCheckIn: lazy(() => import("./component/pages/DemoCheckIn")),
  FlightQuotation: lazy(() => import("./component/pages/FlightQuotation")),
  FlightQuotationPassenger: lazy(
    () => import("./component/pages/FlightQuotationPassenger")
  ),
  FlightQuotationRequest: lazy(
    () => import("./component/pages/FlightQuotationRequest")
  ),
} as const;

const CoreComponents = {
  MagicLogin: lazy(() => import("./component/pages/MagicLogin")),
  Affiliate: lazy(() => import("./component/pages/Affiliate")),
  ActivationCode: lazy(() => import("./component/pages/ActivationCode")),
  TourPackageActivation: lazy(() => import("./component/pages/TourPackageActivation")),
  ToursAndActivities: lazy(() => import("./component/pages/ToursAndActivities")),
  TermsCondition: lazy(() => import("./component/pages/TermsCondition")),
  ManageBooking: lazy(() => import("./component/pages/ManageBooking")),
  BusinessSetting: lazy(() => import("./component/pages/BusinessSetting")),
  ProviderList: lazy(() => import("./component/pages/ProviderList")),
  Logout: lazy(() => import("./component/pages/Logout")),
  FrequentTravellers: lazy(
    () => import("./component/pages/Frequent-Travellers")
  ),
  VerifyData: lazy(() => import("./component/pages/VerifyData")),
  InsuranceIncome: lazy(() => import("./component/pages/InsuranceIncome")),
  IncomeSummary: lazy(() => import("./component/pages/IncomeSummary")),
} as const;

const FlightComponents = {
  DirectBooking: lazy(() => import("./component/pages/DirectBooking")),
  CreateETicket: lazy(() => import("./component/pages/CreateETicket")),
  FlightChange: lazy(() => import("./component/pages/FlightChange")),
  PassengerDetails: lazy(
    () => import("./component/pages/Flight-Outsourcing/PassengerDetails")
  ),
  BookingPassengerDetails: lazy(
    () => import("./component/pages/BookingPassengerDetails")
  ),
  HistoryReport: lazy(() => import("./component/pages/HistoryReport")),
  DirectBookingHistory: lazy(
    () => import("./component/pages/DirectBookingHistory")
  ),
  BookingInquiries: lazy(() => import("./component/pages/BookingInquiries")),
  FlightQuotationList: lazy(
    () => import("./component/pages/FlightQuotationList")
  ),
  AddBaggage: lazy(() => import("./component/pages/AddBaggage")),
  CalendarFlight: lazy(() => import("./component/pages/CalendarFlight")),
} as const;

const HotelComponents = {
  HotelBooking: lazy(() => import("./component/pages/HotelBooking")),
  HotelBookingV2: lazy(() => import("./component/pages/HotelBookingV2")),
  HotelDetailsV2: lazy(() => import("./component/pages/HotelDetailsV2")),
  HotelCheckoutV2: lazy(() => import("./component/pages/HotelCheckoutV2")),
  HotelMyBookingsV2: lazy(() => import("./component/pages/HotelMyBookingsV2")),
  CreateEVoucher: lazy(() => import("./component/pages/CreateEVoucher")),
  EVoucherHistoryReport: lazy(
    () => import("./component/pages/EVoucherHistoryReport")
  ),
  SelectRoom: lazy(() => import("./component/pages/SelectRoom")),
  GuestDetails: lazy(() => import("./component/pages/GuestDetails")),
  HotelBookingHistoryReport: lazy(
    () => import("./component/pages/HotelBookingHistoryReport")
  ),
} as const;

const FinancialComponents = {
  BillingHistory: lazy(() => import("./component/pages/BillingHistory")),
  AddFund: lazy(() => import("./component/pages/AddFund")),
  PaymentHistory: lazy(() => import("./component/pages/PaymentHistory")),
} as const;

const AdminComponents = {
  ELearnings: lazy(() => import("./component/pages/ELearnings")),
  ImageGenerator: lazy(() => import("./component/pages/ImageGenerator")),
  AgentList: lazy(() => import("./component/pages/AgentList")),
  WhitelabelAgentList: lazy(
    () => import("./component/pages/WhitelabelAgentList")
  ),
  WhitelabelBranding: lazy(
    () => import("./component/pages/WhitelabelBranding")
  ),
  AllMemberBookingsReport: lazy(
    () => import("./component/pages/AllMemberBookingsReport")
  ),
  CreateTraining: lazy(() => import("./component/pages/CreateTraining")),
  TrainingList: lazy(() => import("./component/pages/TrainingList")),
  MainCourse: lazy(() => import("./component/pages/MainCourse")),
  TravelFundDeductions: lazy(
    () => import("./component/pages/TravelFundDeductions")
  ),
  TravelFundMonitor: lazy(
    () => import("./component/pages/TravelFundMonitor")
  ),
  TicketFundMonitoring: lazy(
    () => import("./component/pages/TicketFundMonitoring")
  ),
  EmailLogs: lazy(() => import("./component/pages/EmailLogs")),
  BookDemoRequests: lazy(() => import("./component/pages/BookDemoRequests")),
  BusinessVerifications: lazy(
    () => import("./component/pages/BusinessVerifications")
  ),
  SmsMonitor: lazy(() => import("./component/pages/SmsMonitor")),
  ItextSmsMonitor: lazy(() => import("./component/pages/ItextSmsMonitor")),
  UserActivityLogs: lazy(() => import("./component/pages/UserActivityLogs")),
  QueueMonitor: lazy(() => import("./component/pages/QueueMonitor")),
  WithdrawalRequests: lazy(() => import("./component/pages/WithdrawalRequests")),
  WalletMonitoring: lazy(() => import("./component/pages/WalletMonitoring")),
  AgentIncomeMonitoring: lazy(
    () => import("./component/pages/AgentIncomeMonitoring")
  ),
  PaymentDashboard: lazy(() => import("./component/pages/PaymentDashboard")),
} as const;

const VisaComponents = {
  VisaProcessing: lazy(() => import("./component/pages/Visa/VisaProcessing")),
  VisaAgentList: lazy(() => import("./component/pages/Visa/VisaAgentList")),
  VisaAdminList: lazy(() => import("./component/pages/Visa/VisaAdminList")),
  CreateVisa: lazy(() => import("./component/pages/Visa/CreateVisa")),
  EditVisa: lazy(() => import("./component/pages/Visa/EditVisa")),
  VisaList: lazy(() => import("./component/pages/Visa/VisaList")),
  RequestList: lazy(() => import("./component/pages/Visa/RequestList")),
} as const;

const PaymentComponents = {
  SuccessPayment: lazy(() => import("./component/pages/Payments/Success")),
  ExpiredPayment: lazy(() => import("./component/pages/Payments/Expired")),
  FailedPayment: lazy(() => import("./component/pages/Payments/Failed")),
  PendingPayment: lazy(() => import("./component/pages/Payments/Pending")),
} as const;

const InsuranceComponents = {
  TravelInsurance: lazy(() => import("./component/pages/TravelInsurance")),
  TravelInsuranceCheckout: lazy(() => import("./component/pages/TravelInsuranceCheckout")),
  TravelInsuranceHistory: lazy(() => import("./component/pages/TravelInsuranceHistoryReport")),
} as const;

// Memoized components for performance optimization
const MemoizedVerifyData = memo(CoreComponents.VerifyData);

// Route configuration - organized by feature groups for better maintainability
const routeGroups: RouteGroup[] = [
  {
    name: "Public Routes",
    routes: [
      { path: "/login", component: PublicComponents.Login },
      { path: "/join", component: PublicComponents.ReferralJoin },
      { path: "/book-demo", component: PublicComponents.BookFreeDemo },
      { path: "/booking-confirmed", component: PublicComponents.DemoBookingConfirmed },
      { path: "/demo-checkin", component: PublicComponents.DemoCheckIn },
      {
        path: "/quotation/:token",
        component: PublicComponents.FlightQuotation,
      },
      {
        path: "/quotation/:token/passenger/:index",
        component: PublicComponents.FlightQuotationPassenger,
      },
      {
        path: "/quotation/:token/request",
        component: PublicComponents.FlightQuotationRequest,
      },
    ],
  },
  {
    name: "Core Routes",
    routes: [
      { path: "/", component: MemoizedVerifyData },
      { path: "/verify/:token", component: MemoizedVerifyData },
      { path: "/dev/magic-login", component: CoreComponents.MagicLogin },
      { path: "/activation-code", component: CoreComponents.ActivationCode },
      { path: "/tour-package-activation", component: CoreComponents.TourPackageActivation },
      { path: "/tours-and-activities", component: CoreComponents.ToursAndActivities },
      { path: "/affiliate-dashboard", component: CoreComponents.Affiliate },
      { path: "/insurance-income", component: CoreComponents.InsuranceIncome },
      { path: "/income-summary", component: CoreComponents.IncomeSummary },
      { path: "/terms-condition", component: CoreComponents.TermsCondition },
      { path: "/manage-booking", component: CoreComponents.ManageBooking },
      { path: "/account-setting", component: CoreComponents.BusinessSetting },
      { path: "/provider-list", component: CoreComponents.ProviderList },
      { path: "/logout", component: CoreComponents.Logout },
      {
        path: "/frequent-travelers",
        component: CoreComponents.FrequentTravellers,
      },
    ],
  },
  {
    name: "Flight Management Routes",
    routes: [
      { path: "/flight-booking", component: FlightComponents.DirectBooking },
      {
        path: "/e-ticket-generator",
        component: FlightComponents.CreateETicket,
        rebranding: true,
      },
      { path: "/flight-change", component: FlightComponents.FlightChange, rebranding: true },
      { path: "/add-baggage", component: FlightComponents.AddBaggage, rebranding: true },
      {
        path: "/passenger-details",
        component: FlightComponents.PassengerDetails,
      },
      {
        path: "/booking-details",
        component: FlightComponents.BookingPassengerDetails,
      },
      {
        path: "/e-ticket-history-report",
        component: FlightComponents.HistoryReport,
        rebranding: true,
      },
      {
        path: "/booking-inquiries",
        component: FlightComponents.BookingInquiries,
      },
      {
        path: "/flight-quotations",
        component: FlightComponents.FlightQuotationList,
      },
      {
        path: "/booking-history-report",
        component: FlightComponents.DirectBookingHistory,
      },
      { path: "/calendar-flight", component: FlightComponents.CalendarFlight },
    ],
  },
  {
    name: "Hotel Management Routes",
    routes: [
      { path: "/hotel-booking", component: HotelComponents.HotelBooking },
      {
        path: "/hotel-booking-v2",
        component: HotelComponents.HotelBookingV2,
      },
      {
        path: "/hotel-details-v2/:id",
        component: HotelComponents.HotelDetailsV2,
      },
      {
        path: "/hotel-checkout-v2",
        component: HotelComponents.HotelCheckoutV2,
      },
      {
        path: "/hotel-my-bookings-v2",
        component: HotelComponents.HotelMyBookingsV2,
      },
      {
        path: "/e-voucher-generator",
        component: HotelComponents.CreateEVoucher,
        rebranding: true,
      },
      {
        path: "/e-voucher-history-report",
        component: HotelComponents.EVoucherHistoryReport,
        rebranding: true,
      },
      { path: "/select-room", component: HotelComponents.SelectRoom },
      { path: "/guest-details", component: HotelComponents.GuestDetails },
      {
        path: "/hotel-booking-history-report",
        component: HotelComponents.HotelBookingHistoryReport,
      },
    ],
  },
  {
    name: "Financial Routes",
    routes: [
      {
        path: "/billing-history",
        component: FinancialComponents.BillingHistory,
      },
      { path: "/travel-fund", component: FinancialComponents.AddFund },
      {
        path: "/payment-history",
        component: FinancialComponents.PaymentHistory,
      },
    ],
  },
  {
    name: "Admin & Training Routes",
    routes: [
      { path: "/e-learnings", component: AdminComponents.ELearnings },
      { path: "/agent-list", component: AdminComponents.AgentList },
      {
        path: "/agent-list/whitelabel",
        component: AdminComponents.WhitelabelAgentList,
      },
      {
        path: "/branding-setup",
        component: AdminComponents.WhitelabelBranding,
      },
      {
        path: "/all-member-bookings-report",
        component: AdminComponents.AllMemberBookingsReport,
      },
      { path: "/create-training", component: AdminComponents.CreateTraining },
      { path: "/training/edit/:id", component: AdminComponents.CreateTraining },
      { path: "/trainings", component: AdminComponents.TrainingList },
      { path: "/training/main-course", component: AdminComponents.MainCourse },
      {
        path: "/travel-fund-deductions",
        component: AdminComponents.TravelFundDeductions,
      },
      {
        path: "/travelfund-monitor",
        component: AdminComponents.TravelFundMonitor,
      },
      {
        path: "/ticket-fund-monitoring",
        component: AdminComponents.TicketFundMonitoring,
      },
      { path: "/email-logs", component: AdminComponents.EmailLogs },
      { path: "/book-demo-requests", component: AdminComponents.BookDemoRequests },
      {
        path: "/business-verifications",
        component: AdminComponents.BusinessVerifications,
      },
      { path: "/sms-monitor", component: AdminComponents.SmsMonitor },
      { path: "/itext-sms-monitor", component: AdminComponents.ItextSmsMonitor },
      { path: "/user-activity", component: AdminComponents.UserActivityLogs },
      { path: "/user-activity/:userId", component: AdminComponents.UserActivityLogs },
      { path: "/queue-monitor", component: AdminComponents.QueueMonitor },
      { path: "/withdrawal-requests", component: AdminComponents.WithdrawalRequests },
      { path: "/wallet-monitoring", component: AdminComponents.WalletMonitoring },
      { path: "/agent-income-monitoring", component: AdminComponents.AgentIncomeMonitoring },
      { path: "/image-generator", component: AdminComponents.ImageGenerator },
      { path: "/payment-dashboard", component: AdminComponents.PaymentDashboard },
    ],
  },
  {
    name: "Visa Processing Routes",
    routes: [
      { path: "/visa/request/:id", component: VisaComponents.VisaProcessing },
      { path: "/visa/create", component: VisaComponents.CreateVisa },
      { path: "/visa/edit/:id", component: VisaComponents.EditVisa },
      { path: "/visa/book", component: VisaComponents.VisaList },
      { path: "/visa/list", component: VisaComponents.VisaAgentList },
      { path: "/visa/process/list", component: VisaComponents.VisaAdminList },
      { path: "/visa/request/list", component: VisaComponents.RequestList },
    ],
  },
  {
    name: "Insurance Routes",
    routes: [
      { path: "/travel-insurance", component: InsuranceComponents.TravelInsurance },
      { path: "/travel-insurance/checkout", component: InsuranceComponents.TravelInsuranceCheckout },
      { path: "/travel-insurance/history", component: InsuranceComponents.TravelInsuranceHistory },
    ],
  },
  {
    name: "Payment Processing",
    routes: [
      { path: "/payment/success", component: PaymentComponents.SuccessPayment },
      { path: "/payment/expired", component: PaymentComponents.ExpiredPayment },
      { path: "/payment/failed", component: PaymentComponents.FailedPayment },
      { path: "/payment/pending", component: PaymentComponents.PendingPayment },
    ],
  },
];

const RebrandingGuard: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const currentUser = useSelector((state: any) => state.currentUser);
  return canUseRebranding(currentUser) ? (
    <>{children}</>
  ) : (
    <Navigate to="/flight-booking" replace />
  );
};

// Utility function to render a route
const renderRoute = (route: RouteConfig, index: number) => {
  const Component = route.component;
  return (
    <Route
      key={`${route.path}-${index}`}
      path={route.path}
      element={
        route.rebranding ? (
          <RebrandingGuard>
            <Component />
          </RebrandingGuard>
        ) : (
          <Component />
        )
      }
    />
  );
};

// Custom error boundary for better error handling
const RouteErrorBoundary: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <Suspense fallback={<SpinnerLoader />}>{children}</Suspense>;
};

const App: React.FC = () => {
  return (
    <Router>
      <Header />
      <WhitelabelFavicon />
      <RouteErrorBoundary>
        <Routes>
          {/* Render all route groups */}
          {routeGroups.map((group) =>
            group.routes.map((route, index) => renderRoute(route, index))
          )}

          {/* 404 Page - Must be last */}
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </RouteErrorBoundary>
    </Router>
  );
};

export default App;
